/**
 * Physical Process Create Route
 *
 * Create a new physical process using PhysicalProcessForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { PhysicalProcessForm } from '../../components/physical-processes';
import {
  createPhysicalProcess,
  type CreatePhysicalProcessInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreatePhysicalProcessFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/physical-processes/new')({
  component: PhysicalProcessCreatePage,
});

function PhysicalProcessCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreatePhysicalProcessFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreatePhysicalProcessInput = {
        name: formData.name,
        description: formData.description || undefined,
        processType: formData.processType,
        processTier: formData.processTier,
        processOwnerId: formData.processOwnerId,
        owningBusinessUnitId: formData.owningBusinessUnitId,
        automationLevel: formData.automationLevel || undefined,
        status: formData.status || undefined,
        estimatedDuration: formData.estimatedDuration || undefined,
        volumePerPeriod: formData.volumePerPeriod || undefined,
        inputDescription: formData.inputDescription || undefined,
        outputDescription: formData.outputDescription || undefined,
        toolingDescription: formData.toolingDescription || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      const newPhysicalProcess = await createPhysicalProcess(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListPhysicalProcesses'],
      });

      // Navigate to the new physical process detail page
      navigate({
        to: `/physical-processes/${newPhysicalProcess.entityId}` as string,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/physical-processes', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="physical-process-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Physical Process</h1>
        <p className="forge-typography--body2 page-header-subtitle">Add a new physical process</p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <PhysicalProcessForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
